import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any;
  onSuccess: () => void;
}

const AVAILABLE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'site_admin', label: 'Site Admin' },
  { value: 'chair', label: 'Chair' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'school_liaison', label: 'School Liaison' },
  { value: 'communications', label: 'Communications' },
  { value: 'catering', label: 'Catering' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'user', label: 'User' },
];

export function EditUserDialog({ open, onOpenChange, user, onSuccess }: EditUserDialogProps) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [adminNotes, setAdminNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        setFullName(user.full_name || "");
        setEmail(user.email || "");
        setAdminNotes(user.admin_notes || "");
        
        // Fetch roles using the actual user_id from auth, not the member id
        if (user.user_id) {
          const { data: rolesData } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", user.user_id);
          
          setSelectedRoles(rolesData?.map((r: any) => r.role) || []);
        } else {
          setSelectedRoles([]);
        }
      }
    };
    
    loadUserData();
  }, [user]);

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Update profile
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        full_name: fullName,
        email: email
      })
      .eq('id', user.user_id);

    if (profileError) {
      setLoading(false);
      toast({
        title: "Error",
        description: profileError.message,
        variant: "destructive"
      });
      return;
    }

    // Update admin notes in members table
    const { error: notesError } = await supabase
      .from('members')
      .update({
        admin_notes: adminNotes
      })
      .eq('id', user.id);

    if (notesError) {
      setLoading(false);
      toast({
        title: "Error",
        description: notesError.message,
        variant: "destructive"
      });
      return;
    }

    // Get current roles from database using user_id
    const { data: currentRolesData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.user_id);
    
    const currentRoles = currentRolesData?.map((r: any) => r.role) || [];
    
    console.log('=== ROLE SAVE DEBUG ===');
    console.log('User ID:', user.user_id);
    console.log('Current roles:', currentRoles);
    console.log('Selected roles:', selectedRoles);
    
    // Roles to add
    const rolesToAdd = selectedRoles.filter(role => !currentRoles.includes(role));
    
    // Roles to remove
    const rolesToRemove = currentRoles.filter((role: string) => !selectedRoles.includes(role));

    console.log('Roles to add:', rolesToAdd);
    console.log('Roles to remove:', rolesToRemove);

    // Add new roles
    if (rolesToAdd.length > 0) {
      console.log('Inserting roles:', rolesToAdd.map(role => ({ user_id: user.user_id, role })));
      const { data: insertedData, error: addError } = await supabase
        .from('user_roles')
        .insert(rolesToAdd.map(role => ({ user_id: user.user_id, role: role as any })))
        .select();

      console.log('Insert result:', { insertedData, addError });

      if (addError) {
        setLoading(false);
        toast({
          title: "Error",
          description: addError.message,
          variant: "destructive"
        });
        return;
      }
    }

    // Remove old roles
    if (rolesToRemove.length > 0) {
      console.log('Deleting roles:', rolesToRemove);
      const { error: removeError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', user.user_id)
        .in('role', rolesToRemove);

      console.log('Delete result:', { removeError });

      if (removeError) {
        setLoading(false);
        toast({
          title: "Error",
          description: removeError.message,
          variant: "destructive"
        });
        return;
      }
    }

    console.log('=== ROLE SAVE COMPLETE ===');

    setLoading(false);
    toast({
      title: "Success",
      description: "User updated successfully"
    });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update user information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Smith"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {AVAILABLE_ROLES.map(role => (
                <div key={role.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={role.value}
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={() => toggleRole(role.value)}
                  />
                  <label
                    htmlFor={role.value}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="adminNotes">Site Admin Notes</Label>
            <p className="text-xs text-muted-foreground">Internal notes about this user (only visible to site admins)</p>
            <Textarea
              id="adminNotes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this user (e.g., warnings, reports, behavioral issues)..."
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
